import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Plus, X } from 'lucide-react';
import { Report } from '../../types/dashboard';

interface ReportCustomizationDropdownProps {
  reports: Report[];
  onToggleReport: (reportId: string, isVisible: boolean) => Promise<void>;
  /** Mobile bottom sheet: parent controls visibility; same list + onToggleReport as desktop. */
  variant?: 'default' | 'sheet';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const ReportCustomizationDropdown: React.FC<ReportCustomizationDropdownProps> = ({
  reports,
  onToggleReport,
  variant = 'default',
  open: openProp,
  onOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isSheet = variant === 'sheet';
  const isOpen = isSheet ? Boolean(openProp) : internalOpen;

  const [loading, setLoading] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!dropdownRef.current?.contains(target)) {
        if (isSheet) {
          onOpenChange?.(false);
        } else if (buttonRef.current && !buttonRef.current.contains(target)) {
          setInternalOpen(false);
        }
      }
    };

    const handleScroll = (event: Event) => {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        if (isSheet) {
          onOpenChange?.(false);
        } else {
          setInternalOpen(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, isSheet, onOpenChange]);

  const handleDropdownToggle = () => {
    if (isSheet) {
      return;
    }
    if (!internalOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right - 320 + window.scrollX // 320px is dropdown width
      });
    }
    setInternalOpen(!internalOpen);
  };

  const handleToggle = async (reportId: string, currentlyVisible: boolean) => {
    setLoading(reportId);
    try {
      await onToggleReport(reportId, !currentlyVisible);
    } catch (_error) {
    } finally {
      setLoading(null);
    }
  };

  const listContent = (
    <>
      <div className="px-4 py-2 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 uppercase">Customize Reports</h3>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {reports.map((report) => (
          <div
            key={report.id}
            className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
          >
            <span className="text-sm text-gray-800 flex-1">{report.title}</span>
            <button
              type="button"
              onClick={() => handleToggle(report.id, report.isVisible)}
              disabled={loading === report.id}
              className={`flex items-center gap-2 px-3 py-1 rounded text-xs font-medium transition-colors ${
                report.isVisible
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              } ${loading === report.id ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {loading === report.id ? (
                <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : report.isVisible ? (
                <>
                  <X size={14} />
                  <span>REMOVE</span>
                </>
              ) : (
                <>
                  <Plus size={14} />
                  <span>ADD</span>
                </>
              )}
            </button>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="relative">
      {!isSheet && (
        <button
          type="button"
          ref={buttonRef}
          onClick={handleDropdownToggle}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
        >
          <span className="text-sm-nova">Add/Remove Reports</span>
          <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      )}

      {isOpen && !isSheet && createPortal(
        <div
          ref={dropdownRef}
          className="fixed bg-white border border-gray-300 rounded-lg shadow-lg py-2 w-80 z-[9999]"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
          }}
        >
          {listContent}
        </div>,
        document.body
      )}

      {isOpen && isSheet && createPortal(
        <div
          ref={dropdownRef}
          className="fixed bottom-0 left-0 right-0 z-[9999] bg-white border-t border-gray-300 rounded-t-xl shadow-lg max-h-[85vh] flex flex-col pt-2 pb-6"
        >
          {listContent}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ReportCustomizationDropdown;
